(ns app.front.view.group.reporting.reviews
  (:require [app.domain.group.model :as group-model]
            [app.domain.review.model :as review-model]
            [app.front.entities :as entities]
            [app.front.ui.content-switcher :as ui-cs]
            [app.front.ui.filter-common :as filter-common]
            [app.front.util :refer [for-frag]]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.group.reporting.common :as reporting-common]
            [app.front.view.group.reporting.download-button :as rdb]
            [app.front.view.group.reporting.filters :as reporting-filters]
            [app.front.view.group.reporting.form-fields :as reporting-ff]
            [app.front.view.group.reporting.list :as report-list]
            [app.query.filter-util :as filter-util]
            [app.reporting.model :as reporting-model]
            [clojure.set :as set]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))


(defn optional-fields []
  (cond-> [:reporting/municipality
           :reporting/submitted-date
           :reporting/ready-to-issue-date
           :reporting/issued-date
           :reporting/issued-by
           :reporting/requested-date
           :reporting/completed-date
           :reporting/due-date
           :reporting/time-in-review
           :reporting/changes-requested
           :reporting/municipal-review]
    (ff/enabled? :feature-flag.id/property-db-units)
    (conj :reporting/application-unit-type-and-number)))


(def default-settings
  (let [report-type (-> reporting-model/review-report-types first)]
    {:categories               #{}
     :sort-by                  #{(-> reporting-model/review-sort-options report-type first)}
     :report-type              #{report-type}
     :date-selector-mode       #{:reporting.date-range-options/current-month}
     :review-configs           #{}
     :selected-optional-fields #{}}))


(rf/reg-event-db ::set-selected
  (fn [db [_ k v]]
    (assoc-in db [::data k] v)))


(rf/reg-sub ::data :-> ::data)


(rf/reg-sub ::report-type
  :<- [::data]
  (fn [data _]
    (::report-type data (first reporting-model/review-report-types))))


(rf/reg-sub ::available-sort-options
  :<- [::report-type]
  (fn [report-type _]
    (get reporting-model/review-sort-options report-type)))


(rf/reg-sub ::sort-option
  :<- [::data]
  :<- [::available-sort-options]
  (fn [[data sort-options] _]
    (or (some->> data
                 ::sort-option
                 ((set sort-options)))
        (first sort-options))))


(rf/reg-sub ::selected-review-configs
  :<- [::data]
  #(-> % ::selected-review-configs set))


(rf/reg-event-fx ::select-saved-filter
  (fn [{:keys [db]} [_ selected-filter]]
    (let [filter-values      (filter-util/filter-entity->search-values-map selected-filter)
          type               (->> filter-values :report-type first)
          sort               (->> filter-values :sort-by first)
          date-selector-mode (->> filter-values :date-selector-mode first)]
      {:dispatch-n [[::reporting-common/set-selected-categories (:categories filter-values)]
                    [::set-selected ::selected-review-configs (:review-configs filter-values)]
                    [::reporting-common/set-category-missing-selected (first (:include-category-missing filter-values))]
                    [::reporting-ff/set-selected-from-filter filter-values]]
       :db         (-> db
                       (update ::data #(p/assoc-if %
                                                   ::report-type type
                                                   ::sort-option sort))
                       (p/assoc-if ::reporting-common/date-selector-mode date-selector-mode
                                   ::reporting-common/selected-optional-fields (:optional-fields filter-values)))})))


(rf/reg-event-db ::initialize-settings
  (fn [db _]
    (let [{:keys [categories sort-by report-type selected-optional-fields review-configs date-selector-mode]} default-settings]
      (-> db
          (update ::data #(assoc %
                                 ::report-type (first report-type)
                                 ::selected-review-configs review-configs
                                 ::sort-option (first sort-by)))
          (assoc ::reporting-common/date-selector-mode (first date-selector-mode)
                 ::reporting-common/selected-categories categories
                 ::reporting-common/category-missing-selected? false
                 ::reporting-common/selected-optional-fields selected-optional-fields
                 ::reporting-ff/selected nil)))))


(rf/reg-sub ::filter-settings
  :<- [::gas/group]
  :<- [::reporting-common/selected-categories]
  :<- [::report-type]
  :<- [::sort-option]
  :<- [::reporting-common/date-selector-mode]
  :<- [::selected-review-configs]
  :<- [::reporting-common/selected-optional-fields]
  :<- [::reporting-common/category-missing-selected?]
  :<- [::reporting-ff/sorted-selected-fields]
  (fn [[group
        categories
        report-type
        sort-by
        date-selector-mode
        review-configs
        optional-fields
        category-missing-selected?
        form-fields] _]
    (merge (util/mmap {:report-type        #{report-type}
                       :sort-by            #{sort-by}
                       :date-selector-mode #{date-selector-mode}
                       :group-id           #{(:db/id group)}}
                      categories
                      review-configs
                      optional-fields
                      form-fields)
           (when category-missing-selected?
             {:include-category-missing #{category-missing-selected?}}))))


(rf/reg-sub ::filters-in-default-state?
  :<- [::filter-settings]
  (fn [filter-settings]
    (= (dissoc filter-settings :group-id) default-settings)))


(rf/reg-sub ::conditions
  :<- [::gas/group-id]
  :<- [::filter-settings]
  (fn [[group-id filter-settings]]
    (-> filter-settings
        (assoc :group-id #{group-id})
        filter-common/filter-values->conditions)))


(rf/reg-sub ::query
  :<- [::gas/group-id]
  :<- [::report-type]
  :<- [::reporting-common/selected-categories]
  :<- [::reporting-common/category-missing-selected?]
  :<- [::selected-review-configs]
  :<- [::reporting-common/date-range]
  :<- [::sort-option]
  :<- [::reporting-common/selected-optional-fields]
  :<- [::reporting-ff/sorted-selected-fields]
  (fn [[group-id report-type categories category-missing-selected? review-configs
        [start-date end-date] sort-option optional-fields form-fields] _]
    (-> {:group-id    group-id
         :report-type report-type}
        (p/assoc-if :categories (seq categories)
                    :include-category-missing category-missing-selected?
                    :review-configs (seq review-configs)
                    :start-date start-date
                    :end-date end-date
                    :sorting sort-option
                    :optional-fields optional-fields
                    :form-fields (seq form-fields)))))


(rf/reg-sub ::combined-review-configs
  :<- [::entities/context]
  :<- [::gas/group]
  :<- [:language]
  :<- [::gas/superdomain-group?]
  (fn [[ctx group lang superdomain?] _]
    (let [groups   (if superdomain?
                     (->> group
                          group-model/descendant-authority-groups
                          set)
                     #{group})
          entities (->> (entities/find-entities ctx {:entity/type :entity.type/review-config})
                        (filter #(contains? groups (:domain/authority-group %))))
          name-fn  #(review-model/review-config-name % lang)]
      (reporting-common/combined-report-data entities name-fn))))


(defn review-name-selector [tr]
  (let [combined-review-configs (<sub [::combined-review-configs])
        selected-review-configs (<sub [::selected-review-configs])
        all-review-ids          (->> combined-review-configs
                                     (map last)
                                     (apply set/union))
        on-select-all           #(>evt [::set-selected ::selected-review-configs all-review-ids])
        on-deselect-all         #(>evt [::set-selected ::selected-review-configs #{}])]
    [:div
     [reporting-common/selector-header tr :reporting.reviews/review-names on-select-all on-deselect-all]
     [:div.flex.flex-row.flex-wrap
      (for-frag [[review-name id-set] combined-review-configs]
        [:div.w-56
         (cc/checkbox {:id         (str id-set "-checkbox")
                       :label-text review-name
                       :checked    (set/subset? id-set selected-review-configs)
                       :on-change  #(>evt [::set-selected
                                           ::selected-review-configs
                                           ((if % set/union set/difference) selected-review-configs id-set)])})])]]))


(defn sort-selector [tr]
  (when-let [sort-options (seq (<sub [::available-sort-options]))]
    (let [sort-option (<sub [::sort-option])]
      [:div.flex.items-center.mb-6
       [:span.mr-4.whitespace-nowrap (tr [:reporting/sort-by])]
       [ui-cs/content-switcher {:class     "mt-2 mb-2"
                                :items     sort-options
                                :val->str  (comp tr vector)
                                :current   sort-option
                                :on-change #(>evt [::set-selected ::sort-option %])}]])))


(defn view [match]
  (let [tr          (<sub [::gas/group-tr])
        report-type (<sub [::report-type])
        query       (<sub [::query])
        auth-group? (<sub [::gas/auth-group?])]
    [reporting-common/report-page
     match
     tr
     [:section
      [reporting-filters/filters tr {:filter-type             :filter.type/reporting.reviews
                                     :filter-settings-sub     ::filter-settings
                                     :default-filters-sub     ::filters-in-default-state?
                                     :conditions-sub          ::conditions
                                     :select-saved-filter-evt ::select-saved-filter
                                     :initialize-settings-evt ::initialize-settings}]]
     [:section
      [:h3 (tr [:reporting/select-report])]
      [reporting-common/report-type-selector
       {:tr                  tr
        :label-loc           :reporting.reviews/type
        :on-change           #(>evt [::set-selected ::report-type (nth reporting-model/review-report-types %)])
        :report-type-options reporting-model/review-report-types
        :selected-index      (p/find-index reporting-model/review-report-types
                                           #(= % report-type))}]

      [:div.w-full
       [reporting-common/date-range-selector tr "reviews-date-range" reporting-common/date-range-options]]]
     [:section [:div.w-full.mb-6
                [reporting-common/category-selector tr true]]
      [:div.w-full.mb-8
       [review-name-selector tr]]

      [:div.w-full.mb-8
       [reporting-common/optional-field-selector tr :reviews (optional-fields)]]

      (when (or auth-group?
                (ff/enabled? :feature-flag.id/superdomain-can-manage-forms))
        [:div.w-full.mb-8
         [reporting-ff/form-field-selector tr false]])]

     [:div.w-full
      [sort-selector tr]]

     [report-list/list-section (util/mmap tr report-type)]

     [rdb/generate-button (util/mmap tr query)]]))


(def route
  {:name        :view/group.reporting.reviews
   :view        view
   :parameters  {:path {:group-id s/Int}}
   :controllers [{:parameters {:path [:group-id]}
                  :start      (fn [params]
                                (let [group-id (-> params :path :group-id)]
                                  (>evt [::reporting-filters/fetch-saved-filters group-id])
                                  (>evt [::reporting-common/init group-id (first reporting-common/date-range-options)])))
                  :stop       (fn []
                                (>evt [::initialize-settings]))}]})
